import React, { useState, useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Upload, Download, MessageCircle, Mail, Filter, X } from 'lucide-react';
import { downloadExport } from '../utils/exportUtils';
import { buildWaUrl } from '../utils/whatsapp';
import { calcInternalDue, internalDueColor, DEFAULT_BUFFERS } from '../utils/dueDate';
import api from '../utils/api';
import usePersistedFilter from '../utils/usePersistedFilter';
import { ColFilterDropdown, FilterTh, InlineDropdown, EditableCell } from '../components/gridShared';
import { fmtDate, fmtCurrency, FY_OPTIONS, CURRENT_FY, statusColor, MONTHS_FY, canDelete, canImport } from '../utils/helpers';
import { PageHeader, DataTable, SearchBar, StatusBadge, Modal, FormField } from '../components/Table';
import DueDateEditModal from '../components/DueDateEditModal';
import ClientSearchDropdown from '../components/ClientSearchDropdown';
import ConfirmModal from '../components/ConfirmModal';
import ModuleImport from '../components/ModuleImport';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import BulkActionBar from '../components/BulkActionBar';
import BulkEmailModal from '../components/BulkEmailModal';

const GST_RETURN_TYPES = ['GSTR-1', 'GSTR-3B', 'GSTR-9', 'GSTR-9C', 'CMP-08', 'GSTR-4'];
const GST_STATUSES = ['Data Pending', 'Data Received', 'Prepared', 'Filed', 'Filed with Late Fee'];
const NOTICE_TYPES = ['ASMT-10', 'DRC-01', 'DRC-01A', 'SCN', 'Audit Notice', 'Inspection', 'Others'];
const NOTICE_STATUSES = ['Received', 'Reply Drafted', 'Submitted', 'Personal Hearing', 'Order Received', 'Appeal Filed', 'Resolved'];

// ── Grid column config ─────────────────────────────────────────────────────────
const ALL_COLS = [
  'client_code', 'client_name', 'entity_type',
  'fy', 'period', 'return_type', 'primary_assignee_id', 'status',
  'effective_due_date', 'filing_date', 'internal_due_date', 'late_fee',
  'act_edit', 'act_whatsapp', 'act_email', 'act_delete',
];
const DROPDOWN_COLS = new Set(['fy', 'return_type', 'primary_assignee_id', 'status']);
const DATE_COLS = new Set(['filing_date', 'internal_due_date']);
const TEXT_COLS = new Set(['period', 'late_fee']);
const READONLY_COLS = new Set(['client_code', 'client_name', 'entity_type', 'effective_due_date']);
const ACTION_COLS = new Set(['act_edit', 'act_whatsapp', 'act_email', 'act_delete']);

function filterMatches(row, colFilters) {
  for (const [col, f] of Object.entries(colFilters)) {
    if (!f) continue;
    if (Array.isArray(f)) { if (f.length && !f.includes(String(row[col] ?? ''))) return false; }
    else if (typeof f === 'object') { const v = row[col] ?? ''; if (f.from && v < f.from) return false; if (f.to && v > f.to) return false; }
    else if (!String(row[col] ?? '').toLowerCase().includes(f.toLowerCase())) return false;
  }
  return true;
}

// ── WhatsApp Modal ─────────────────────────────────────────────────────────────
function WhatsAppModal({ targets, defaultMsg, onClose }) {
  const isBulk = targets.length > 1;
  const [msg, setMsg] = useState(defaultMsg || '');
  const textRef = useRef();
  useEffect(() => { textRef.current?.focus(); }, []);
  const withNumber = targets.filter(t => t.whatsapp);
  const sendAll = () => {
    targets.forEach((t, i) => {
      if (!t.whatsapp) return;
      const url = buildWaUrl(t.whatsapp, msg);
      if (url) setTimeout(() => window.open(url, '_blank'), i * 400);
    });
    onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle size={20} className="text-green-600" />
          <h3 className="font-semibold text-gray-900">
            {isBulk ? `WhatsApp — ${withNumber.length} of ${targets.length} clients` : `WhatsApp — ${targets[0]?.name}`}
          </h3>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {isBulk && (
          <div className="mb-3 max-h-24 overflow-y-auto border border-gray-100 rounded-lg divide-y">
            {targets.map((t, i) => (
              <div key={i} className={`px-3 py-1.5 text-xs flex items-center gap-2 ${t.whatsapp ? '' : 'opacity-40'}`}>
                <span className="font-medium text-gray-700 flex-1">{t.name}</span>
                <span className="text-gray-400 font-mono">{t.whatsapp || 'No number'}</span>
              </div>
            ))}
          </div>
        )}
        {!isBulk && targets[0]?.whatsapp && <p className="text-xs text-gray-500 mb-3">+91 {targets[0].whatsapp}</p>}
        <label className="block text-xs font-medium text-gray-700 mb-1">Message</label>
        <textarea ref={textRef} value={msg} onChange={e => setMsg(e.target.value)} rows={7}
          className="input-field text-sm w-full resize-y leading-relaxed mb-4" />
        <div className="flex gap-3">
          <button onClick={sendAll} disabled={withNumber.length === 0}
            className="flex-1 py-2 rounded-lg font-medium text-sm bg-green-600 hover:bg-green-700 text-white disabled:opacity-40 flex items-center justify-center gap-2">
            <MessageCircle size={14} />
            {isBulk ? `Send to ${withNumber.length} client${withNumber.length !== 1 ? 's' : ''}` : 'Send via WhatsApp'}
          </button>
          <button onClick={onClose} className="flex-1 py-2 rounded-lg font-medium text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── EntryRow ───────────────────────────────────────────────────────────────────
const EntryRow = forwardRef(function EntryRow({ staff, onSaved, onFocusUp, buffers }, ref) {
  const blank = useCallback(() => ({
    client_id: null, fy: CURRENT_FY, period: '', return_type: 'GSTR-3B',
    primary_assignee_id: '', status: 'Data Pending',
    filing_date: '', internal_due_date: '', late_fee: '',
  }), []);
  const [row, setRow] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [activeCol, setActiveCol] = useState(null);
  const cellRefs = useRef({});

  useImperativeHandle(ref, () => ({
    focusCol: col => setTimeout(() => { cellRefs.current[col]?.focus() || cellRefs.current['client']?.focus(); }, 0)
  }));

  useEffect(() => {
    if (clientSearch.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/clients', { params: { search: clientSearch, limit: 8 } });
        setSuggestions(r.data?.clients || r.data || []);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const selectClient = c => {
    setRow(r => ({ ...r, client_id: c.id }));
    setClientSearch(c.name); setShowSug(false);
    setTimeout(() => cellRefs.current['fy']?.focus(), 0);
  };

  const ENTRY_COLS = ['fy', 'period', 'return_type', 'primary_assignee_id', 'status', 'filing_date', 'internal_due_date', 'late_fee'];
  const tabHandler = (col, e) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); onFocusUp?.(col); return; }
    if (e.key !== 'Tab' && e.key !== 'Enter') return;
    const idx = ENTRY_COLS.indexOf(col);
    if (e.key === 'Enter') { e.preventDefault(); if (row.client_id) save(); return; }
    e.preventDefault();
    if (!e.shiftKey) {
      if (idx < ENTRY_COLS.length - 1) cellRefs.current[ENTRY_COLS[idx + 1]]?.focus();
      else if (row.client_id) save();
    } else {
      if (idx > 0) cellRefs.current[ENTRY_COLS[idx - 1]]?.focus();
      else cellRefs.current['client']?.focus();
    }
  };

  const cls = col => `table-cell text-xs ${activeCol === col ? 'ring-2 ring-inset ring-[#1e3a5f]/30' : 'bg-amber-50/30'}`;

  const save = async () => {
    if (!row.client_id) { setError('Select a client'); return; }
    if (!row.period.trim()) { setError('Period required'); cellRefs.current['period']?.focus(); return; }
    setSaving(true); setError('');
    try {
      await api.post('/gst/returns', { ...row });
      setRow(blank()); setClientSearch(''); onSaved(); toast.success('GST return added');
    } catch (e) {
      if (e?.response?.status === 409) setError('Duplicate');
      else setError(e?.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <tr className="border-t-2 border-dashed border-[#1e3a5f]/20 bg-amber-50/20">
      <td className="table-cell w-8" />
      {/* Client */}
      <td className={`table-cell relative ${activeCol === 'client' ? 'ring-2 ring-inset ring-[#1e3a5f]/30' : 'bg-amber-50/30'}`} colSpan={3}>
        <input ref={el => { cellRefs.current['client'] = el; }} value={clientSearch}
          onChange={e => { setClientSearch(e.target.value); setShowSug(true); setRow(r => ({ ...r, client_id: null })); }}
          onFocus={() => setActiveCol('client')}
          onKeyDown={e => {
            if (e.key === 'ArrowUp') { e.preventDefault(); onFocusUp?.('client_name'); }
            if (e.key === 'Tab' && !e.shiftKey) { e.preventDefault(); cellRefs.current['fy']?.focus(); }
          }}
          placeholder="Type client name…"
          className="w-full text-xs bg-transparent border-0 outline-none placeholder-gray-300" />
        {showSug && suggestions.length > 0 && (
          <div className="absolute z-40 left-0 top-full bg-white border border-gray-200 rounded-xl shadow-xl w-72 max-h-44 overflow-y-auto">
            {suggestions.map(c => (
              <div key={c.id} onMouseDown={() => selectClient(c)} className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm">
                <span className="font-medium text-gray-800">{c.name}</span>
                {c.client_code && <span className="text-xs text-gray-400 ml-2">{c.client_code}</span>}
              </div>
            ))}
          </div>
        )}
      </td>
      {/* FY */}
      <td className={cls('fy')}>
        <select ref={el => { cellRefs.current['fy'] = el; }} value={row.fy}
          onChange={e => setRow(r => ({ ...r, fy: e.target.value }))}
          onFocus={() => setActiveCol('fy')} onKeyDown={e => tabHandler('fy', e)}
          className="w-full text-xs bg-transparent border-0 outline-none">
          {FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
        </select>
      </td>
      {/* Period */}
      <td className={cls('period')}>
        <input ref={el => { cellRefs.current['period'] = el; }} type="text" value={row.period}
          placeholder="Apr 2025"
          onChange={e => setRow(r => ({ ...r, period: e.target.value }))}
          onFocus={() => setActiveCol('period')} onKeyDown={e => tabHandler('period', e)}
          className="w-full text-xs bg-transparent border-0 outline-none placeholder-gray-200 p-0" />
      </td>
      {/* Return Type */}
      <td className={cls('return_type')}>
        <select ref={el => { cellRefs.current['return_type'] = el; }} value={row.return_type}
          onChange={e => setRow(r => ({ ...r, return_type: e.target.value }))}
          onFocus={() => setActiveCol('return_type')} onKeyDown={e => tabHandler('return_type', e)}
          className="w-full text-xs bg-transparent border-0 outline-none">
          {GST_RETURN_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
      </td>
      {/* Assignee */}
      <td className={cls('primary_assignee_id')}>
        <select ref={el => { cellRefs.current['primary_assignee_id'] = el; }} value={row.primary_assignee_id}
          onChange={e => setRow(r => ({ ...r, primary_assignee_id: e.target.value }))}
          onFocus={() => setActiveCol('primary_assignee_id')} onKeyDown={e => tabHandler('primary_assignee_id', e)}
          className="w-full text-xs bg-transparent border-0 outline-none">
          <option value="">—</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </td>
      {/* Status */}
      <td className={cls('status')}>
        <select ref={el => { cellRefs.current['status'] = el; }} value={row.status}
          onChange={e => setRow(r => ({ ...r, status: e.target.value }))}
          onFocus={() => setActiveCol('status')} onKeyDown={e => tabHandler('status', e)}
          className="w-full text-xs bg-transparent border-0 outline-none">
          {GST_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </td>
      {/* Due — auto */}
      <td className="table-cell bg-amber-50/30"><span className="text-xs text-gray-300">auto</span></td>
      {/* Filing date */}
      <td className={cls('filing_date')}>
        <input ref={el => { cellRefs.current['filing_date'] = el; }} type="date" value={row.filing_date}
          onChange={e => setRow(r => ({ ...r, filing_date: e.target.value }))}
          onFocus={() => setActiveCol('filing_date')} onKeyDown={e => tabHandler('filing_date', e)}
          className="w-full text-xs bg-transparent border-0 outline-none p-0" />
      </td>
      {/* Internal due */}
      <td className={cls('internal_due_date')}>
        <input ref={el => { cellRefs.current['internal_due_date'] = el; }} type="date" value={row.internal_due_date}
          onChange={e => setRow(r => ({ ...r, internal_due_date: e.target.value }))}
          onFocus={() => setActiveCol('internal_due_date')} onKeyDown={e => tabHandler('internal_due_date', e)}
          className="w-full text-xs bg-transparent border-0 outline-none p-0" />
      </td>
      {/* Late fee */}
      <td className={cls('late_fee')}>
        <input ref={el => { cellRefs.current['late_fee'] = el; }} type="number" value={row.late_fee}
          placeholder="0"
          onChange={e => setRow(r => ({ ...r, late_fee: e.target.value }))}
          onFocus={() => setActiveCol('late_fee')} onKeyDown={e => tabHandler('late_fee', e)}
          className="w-full text-xs bg-transparent border-0 outline-none placeholder-gray-200 p-0" />
      </td>
      {/* Save */}
      <td className="table-cell" colSpan={4}>
        {error ? <span className="text-xs text-red-500">{error}</span>
          : saving ? <span className="text-xs text-gray-400">Saving…</span>
          : row.client_id
            ? <button onClick={save} className="text-xs text-emerald-600 hover:text-emerald-800 font-medium border border-emerald-300 rounded px-2 py-0.5">Save ↵</button>
            : <span className="text-xs text-gray-300 italic">fill client</span>}
      </td>
    </tr>
  );
});

// ── GST Returns Table ──────────────────────────────────────────────────────────
function GSTTable({ data, staff, selected, toggleSelect, toggleAll, onEdit, onInlineUpdate, onEntryRowSave, onDelete, onArchive, showArchived, onToggleArchived, onSetDueDate, colFilters, colUniqueValues, openColFilter, onWA, onEmail, buffers }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [activeCell, setActiveCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const containerRef = useRef();
  const entryRowRef = useRef();

  // Alt+I — jump straight to the entry row's first cell
  useEffect(() => {
    const h = () => entryRowRef.current?.focusCol();
    window.addEventListener('pms:new-record', h);
    return () => window.removeEventListener('pms:new-record', h);
  }, []);

  const onSort = key => { setSortDir(d => sortKey === key ? (d === 'asc' ? 'desc' : 'asc') : 'asc'); setSortKey(key); };
  const sorted = useMemo(() => sortKey ? [...data].sort((a, b) => {
    const va = a[sortKey] ?? ''; const vb = b[sortKey] ?? '';
    return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  }) : data, [data, sortKey, sortDir]);

  const activate = (rowId, col) => { setActiveCell({ rowId, col }); setEditingCell(null); containerRef.current?.focus(); };
  const startEdit = (rowId, col) => { if (READONLY_COLS.has(col) || ACTION_COLS.has(col)) return; setActiveCell({ rowId, col }); setEditingCell({ rowId, col }); };
  const cancelEdit = () => { setEditingCell(null); containerRef.current?.focus(); };
  const commitEdit = async (col, val) => {
    if (!editingCell) return;
    await onInlineUpdate(editingCell.rowId, { [col]: val || null });
    setEditingCell(null); setActiveCell({ rowId: editingCell.rowId, col }); containerRef.current?.focus();
  };

  const handleEntryArrowUp = col => {
    if (sorted.length > 0) {
      setActiveCell({ rowId: sorted[sorted.length - 1].id, col: ALL_COLS.includes(col) ? col : ALL_COLS[0] });
      containerRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!activeCell) return;
    containerRef.current?.querySelector(`[data-cell="${activeCell.rowId}-${activeCell.col}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeCell]);

  const handleKeyDown = e => {
    if (!activeCell) return;
    if (editingCell) { if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); } return; }
    const { rowId, col } = activeCell;
    const rowIdx = sorted.findIndex(r => r.id === rowId);
    const colIdx = ALL_COLS.indexOf(col);

    if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); if (DROPDOWN_COLS.has(col)) startEdit(rowId, col); return; }
    if (!e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && DATE_COLS.has(col) && /^\d$/.test(e.key)) { e.preventDefault(); startEdit(rowId, col); return; }

    if (ACTION_COLS.has(col) && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault(); const row = sorted[rowIdx]; if (!row) return;
      if (col === 'act_edit') onEdit(row);
      else if (col === 'act_whatsapp') onWA(row);
      else if (col === 'act_email') onEmail(row);
      else if (col === 'act_delete') onDelete?.(row);
      return;
    }

    switch (e.key) {
      case 'ArrowRight': e.preventDefault();
        setActiveCell(colIdx < ALL_COLS.length - 1 ? { rowId, col: ALL_COLS[colIdx + 1] } : rowIdx < sorted.length - 1 ? { rowId: sorted[rowIdx + 1].id, col: ALL_COLS[0] } : activeCell);
        break;
      case 'ArrowLeft': e.preventDefault();
        setActiveCell(colIdx > 0 ? { rowId, col: ALL_COLS[colIdx - 1] } : rowIdx > 0 ? { rowId: sorted[rowIdx - 1].id, col: ALL_COLS[ALL_COLS.length - 1] } : activeCell);
        break;
      case 'ArrowDown': e.preventDefault();
        if (rowIdx < sorted.length - 1) setActiveCell({ rowId: sorted[rowIdx + 1].id, col });
        else { setActiveCell(null); entryRowRef.current?.focusCol(col); }
        break;
      case 'ArrowUp': e.preventDefault();
        if (rowIdx > 0) setActiveCell({ rowId: sorted[rowIdx - 1].id, col });
        break;
      case 'Tab': e.preventDefault();
        if (e.shiftKey) setActiveCell(colIdx > 0 ? { rowId, col: ALL_COLS[colIdx - 1] } : activeCell);
        else setActiveCell(colIdx < ALL_COLS.length - 1 ? { rowId, col: ALL_COLS[colIdx + 1] } : activeCell);
        break;
      case 'Enter': e.preventDefault();
        if (rowIdx < sorted.length - 1) setActiveCell({ rowId: sorted[rowIdx + 1].id, col });
        else { setActiveCell(null); entryRowRef.current?.focusCol(col); }
        break;
      case 'F2': e.preventDefault(); startEdit(rowId, col); break;
      case 'Escape': setActiveCell(null); break;
      case ' ': e.preventDefault(); if (!ACTION_COLS.has(col)) toggleSelect(rowId); break;
      default: break;
    }
  };

  const thProps = { sortKey, sortDir, onSort, colFilters, openColFilter };
  const staffOptions = [{ value: '', label: '—' }, ...staff.map(s => ({ value: String(s.id), label: s.name }))];
  const actCls = (row, col) => `table-cell cursor-pointer outline-none px-1 text-center ${activeCell?.rowId === row.id && activeCell?.col === col ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`;

  return (
    <div ref={containerRef} tabIndex={0} onKeyDown={handleKeyDown} className="outline-none overflow-x-auto">
      <table className="w-full text-left min-w-[1000px]">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="table-header w-8">
              <input type="checkbox" checked={data.length > 0 && selected.size === data.length} onChange={() => toggleAll(data)} className="rounded" />
            </th>
            <FilterTh col="client_code" label="Code" {...thProps} />
            <FilterTh col="client_name" label="Client" {...thProps} />
            <FilterTh col="entity_type" label="Entity" {...thProps} />
            <FilterTh col="fy" label="FY" {...thProps} />
            <FilterTh col="period" label="Period" {...thProps} />
            <FilterTh col="return_type" label="Return" {...thProps} />
            <FilterTh col="primary_assignee_name" label="Assigned To" {...thProps} />
            <FilterTh col="status" label="Status" {...thProps} />
            <FilterTh col="effective_due_date" label="Due Date" {...thProps} />
            <FilterTh col="filing_date" label="Filed On" {...thProps} />
            <FilterTh col="internal_due_date" label="Internal Due" {...thProps} />
            <FilterTh col="late_fee" label="Late Fee" {...thProps} />
            <th className="table-header w-8 text-center text-gray-300 font-normal text-xs">Edit</th>
            <th className="table-header w-8 text-center text-gray-300 font-normal text-xs">WA</th>
            <th className="table-header w-8 text-center text-gray-300 font-normal text-xs">Email</th>
            <th className="table-header w-8 text-center text-gray-300 font-normal text-xs">Del</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(row => {
            const isSel = selected.has(row.id);
            const isRowActive = activeCell?.rowId === row.id;
            const isCellActive = col => isRowActive && activeCell?.col === col;
            const isCellEditing = col => editingCell?.rowId === row.id && editingCell?.col === col;
            const rowCls = `border-b border-gray-50 ${isSel ? 'bg-amber-50' : isRowActive ? 'bg-blue-50/20' : 'hover:bg-gray-50/50'}`;

            return (
              <tr key={row.id} className={rowCls}>
                <td className="table-cell w-8">
                  <input type="checkbox" checked={isSel} onChange={() => toggleSelect(row.id)} className="rounded" />
                </td>
                <td data-cell={`${row.id}-client_code`} className={`table-cell ${isCellActive('client_code') ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`}
                  tabIndex={0} onClick={() => activate(row.id, 'client_code')}>
                  <span className="text-xs font-mono text-gray-500">{row.client_code || '—'}</span>
                </td>
                <td data-cell={`${row.id}-client_name`} className={`table-cell ${isCellActive('client_name') ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`}
                  tabIndex={0} onClick={() => activate(row.id, 'client_name')}>
                  <span className="font-medium text-gray-800 text-sm">{row.client_name}</span>
                </td>
                <td data-cell={`${row.id}-entity_type`} className={`table-cell ${isCellActive('entity_type') ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`}
                  tabIndex={0} onClick={() => activate(row.id, 'entity_type')}>
                  {row.entity_type ? <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{row.entity_type}</span> : <span className="text-gray-300">—</span>}
                </td>

                <EditableCell row={row} col="fy"
                  displayValue={<span className="text-xs">{row.fy || '—'}</span>}
                  editOptions={{ type: 'select', options: FY_OPTIONS }}
                  isActive={isCellActive('fy')} isEditing={isCellEditing('fy')}
                  onActivate={() => activate(row.id, 'fy')} onStartEdit={() => startEdit(row.id, 'fy')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="period"
                  displayValue={<span className="text-xs">{row.period || <span className="text-gray-300">—</span>}</span>}
                  editOptions={{ type: 'text' }}
                  isActive={isCellActive('period')} isEditing={isCellEditing('period')}
                  onActivate={() => activate(row.id, 'period')} onStartEdit={() => startEdit(row.id, 'period')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="return_type"
                  displayValue={<span className="text-xs font-mono bg-green-50 text-green-700 px-1.5 py-0.5 rounded">{row.return_type || '—'}</span>}
                  editOptions={{ type: 'select', options: GST_RETURN_TYPES }}
                  isActive={isCellActive('return_type')} isEditing={isCellEditing('return_type')}
                  onActivate={() => activate(row.id, 'return_type')} onStartEdit={() => startEdit(row.id, 'return_type')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="primary_assignee_id"
                  displayValue={<span className="text-xs text-gray-600">{row.primary_assignee_name || <span className="text-gray-300">—</span>}</span>}
                  editOptions={{ type: 'select', options: staffOptions }}
                  isActive={isCellActive('primary_assignee_id')} isEditing={isCellEditing('primary_assignee_id')}
                  onActivate={() => activate(row.id, 'primary_assignee_id')} onStartEdit={() => startEdit(row.id, 'primary_assignee_id')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="status"
                  displayValue={<span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(row.status)}`}>{row.status}</span>}
                  editOptions={{ type: 'select', options: GST_STATUSES }}
                  isActive={isCellActive('status')} isEditing={isCellEditing('status')}
                  onActivate={() => activate(row.id, 'status')} onStartEdit={() => startEdit(row.id, 'status')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <td data-cell={`${row.id}-effective_due_date`} className={`table-cell ${isCellActive('effective_due_date') ? 'ring-2 ring-inset ring-[#1e3a5f]/30 bg-blue-50/20' : ''}`}
                  tabIndex={0} onClick={() => activate(row.id, 'effective_due_date')}>
                  <div className="flex items-center gap-1">
                    {row.effective_due_date
                      ? <span className={`text-xs font-medium ${row.revised_due_date ? 'text-orange-600' : 'text-gray-700'}`}>{fmtDate(row.effective_due_date)}</span>
                      : <span className="text-gray-300 text-xs">—</span>}
                    <button onClick={e => { e.stopPropagation(); onSetDueDate(row); }} className="text-gray-300 hover:text-[#f0a500]"><Pencil size={11} /></button>
                  </div>
                </td>

                <EditableCell row={row} col="filing_date"
                  displayValue={<span className="text-xs">{fmtDate(row.filing_date) || <span className="text-gray-300">—</span>}</span>}
                  editOptions={{ type: 'date' }}
                  isActive={isCellActive('filing_date')} isEditing={isCellEditing('filing_date')}
                  onActivate={() => activate(row.id, 'filing_date')} onStartEdit={() => startEdit(row.id, 'filing_date')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="internal_due_date"
                  displayValue={row.internal_due_date ? <span className={`text-xs ${internalDueColor(row.internal_due_date, row.status)}`}>{fmtDate(row.internal_due_date)}</span> : <span className="text-gray-300 text-xs">—</span>}
                  editOptions={{ type: 'date' }}
                  isActive={isCellActive('internal_due_date')} isEditing={isCellEditing('internal_due_date')}
                  onActivate={() => activate(row.id, 'internal_due_date')} onStartEdit={() => startEdit(row.id, 'internal_due_date')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <EditableCell row={row} col="late_fee"
                  displayValue={row.late_fee ? <span className="text-xs text-red-600 font-medium">₹{Number(row.late_fee).toLocaleString()}</span> : <span className="text-gray-300 text-xs">—</span>}
                  editOptions={{ type: 'text' }}
                  isActive={isCellActive('late_fee')} isEditing={isCellEditing('late_fee')}
                  onActivate={() => activate(row.id, 'late_fee')} onStartEdit={() => startEdit(row.id, 'late_fee')}
                  onCommit={commitEdit} onCancel={cancelEdit} />

                <td data-cell={`${row.id}-act_edit`} tabIndex={0} className={actCls(row, 'act_edit')}
                  onClick={() => { activate(row.id, 'act_edit'); onEdit(row); }}>
                  <span className="text-xs text-[#1e3a5f] hover:text-[#f0a500]">Edit</span>
                </td>
                <td data-cell={`${row.id}-act_whatsapp`} tabIndex={0} className={actCls(row, 'act_whatsapp')}
                  onClick={() => { activate(row.id, 'act_whatsapp'); onWA(row); }}>
                  <span className={`flex justify-center ${row.whatsapp ? 'text-gray-400 hover:text-green-500' : 'text-gray-200 cursor-not-allowed'}`}><MessageCircle size={14} /></span>
                </td>
                <td data-cell={`${row.id}-act_email`} tabIndex={0} className={actCls(row, 'act_email')}
                  onClick={() => { activate(row.id, 'act_email'); onEmail(row); }}>
                  <span className={`flex justify-center ${row.email ? 'text-gray-400 hover:text-[#1e3a5f]' : 'text-gray-200 cursor-not-allowed'}`}><Mail size={14} /></span>
                </td>
                <td data-cell={`${row.id}-act_delete`} tabIndex={0} className={actCls(row, 'act_delete')}
                  onClick={() => { activate(row.id, 'act_delete'); onDelete?.(row); }}>
                  {onDelete && <span className="flex justify-center text-gray-300 hover:text-red-500"><Trash2 size={14} /></span>}
                </td>
                {onArchive && <td className="table-cell">
                  <button title={row.is_archived ? 'Unarchive' : 'Archive'} onClick={() => onArchive(row)} className={`text-xs px-1 ${row.is_archived ? 'text-amber-600' : 'text-gray-300 hover:text-amber-600'}`}>{row.is_archived ? '↩' : '📁'}</button>
                </td>}
              </tr>
            );
          })}

          <EntryRow ref={entryRowRef} staff={staff} onSaved={onEntryRowSave} onFocusUp={handleEntryArrowUp} buffers={buffers} />
        </tbody>
      </table>
    </div>
  );
}

// ── GST Page ───────────────────────────────────────────────────────────────────
export default function GSTPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState('returns');
  const [data, setData] = useState([]);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [fyFilter, setFyFilter] = usePersistedFilter('pms_filter_fy_gst');
  const [rtFilter, setRtFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [waModal, setWaModal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [dueDateRecord, setDueDateRecord] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dupWarning, setDupWarning] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const clientFilter = sp.get('client') || '';
  const canDel = canDelete(user);
  const canImp = canImport(user);
  const [buffers, setBuffers] = useState(DEFAULT_BUFFERS);
  useEffect(() => { api.get('/settings/buffer').then(r => setBuffers(b => ({ ...b, ...r.data }))).catch(() => {}); }, []);

  const loadData = useCallback(() => {
    const params = {}; if (fyFilter) params.fy = fyFilter;
    if (clientFilter) params.client_id = clientFilter;
    if (rtFilter) params.return_type = rtFilter;
    if (statusFilter) params.status = statusFilter;
    if (showArchived) params.show_archived = '1';
    api.get('/gst/returns', { params }).then(r => setData(r.data)).catch(() => {});
  }, [fyFilter, rtFilter, statusFilter, clientFilter, showArchived]);

  const [noticeData, setNoticeData] = useState([]);
  const loadNotices = useCallback(() => {
    api.get('/gst/notices', { params: { fy: fyFilter } }).then(r => setNoticeData(r.data)).catch(() => {});
  }, [fyFilter]);

  useEffect(() => { api.get('/auth/users').then(r => setStaff(r.data)); }, []);
  useEffect(() => { if (tab === 'returns') loadData(); }, [tab, loadData]);
  useEffect(() => { if (tab === 'notices') loadNotices(); }, [tab, loadNotices]);

  const handleSave = async (e, force = false) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      if (tab === 'notices') {
        await api.post('/gst/notices', form);
      } else if (editing) {
        await api.put(`/gst/returns/${editing.id}`, form);
      } else {
        await api.post('/gst/returns', { ...form, force });
      }
      toast.success('Saved'); setShowModal(false); setDupWarning(null); setForm({}); setEditing(null);
      tab === 'notices' ? loadNotices() : loadData();
    } catch (e) {
      if (e?.response?.status === 409) { setDupWarning(e.response.data?.error); return; }
      toast.error(e?.response?.data?.error || e.message || 'Failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/gst/returns/${deleteTarget.id}`);
      toast.success('Deleted'); setDeleteTarget(null); loadData();
    } catch { toast.error('Failed'); }
  };

  const handleInlineUpdate = async (id, updates) => {
    try { await api.put(`/gst/returns/${id}`, updates); loadData(); }
    catch { toast.error('Update failed'); }
  };

  const colUniqueValues = useMemo(() => {
    const res = {};
    ['entity_type', 'fy', 'return_type', 'status'].forEach(col => {
      res[col] = [...new Set(data.map(r => String(r[col] ?? '')))].filter(Boolean).sort();
    });
    return res;
  }, [data]);

  const openColFilter = (col, anchorRef) => setOpenFilter({ col, anchorRef });
  const applyColFilter = (col, val) => { setColFilters(f => ({ ...f, [col]: val })); setOpenFilter(null); };

  const filtered = useMemo(() => {
    let d = data;
    if (search) { const s = search.toLowerCase(); d = d.filter(r => r.client_name?.toLowerCase().includes(s) || r.client_code?.toLowerCase().includes(s)); }
    return d.filter(r => filterMatches(r, colFilters));
  }, [data, search, colFilters]);

  const filteredNotices = useMemo(() => {
    if (!search) return noticeData;
    const s = search.toLowerCase();
    return noticeData.filter(r => r.client_name?.toLowerCase().includes(s) || r.client_code?.toLowerCase().includes(s));
  }, [noticeData, search]);

  const toggleSelect = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = rows => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));

  const handleWA = row => {
    if (!row.whatsapp) { toast.error('No WhatsApp number for this client'); return; }
    const due = fmtDate(row.effective_due_date);
    const msg = `Dear ${row.client_name},\n\nYour ${row.return_type} for ${row.period} (FY ${row.fy}) is due on ${due}.\n\nCurrent Status: ${row.status}\n\nPlease share the required data at the earliest.\n\nThank you,\nKrishna Uday & Associates`;
    setWaModal({ targets: [{ name: row.client_name, whatsapp: row.whatsapp }], defaultMsg: msg });
  };
  const handleEmail = row => toast('Email feature coming soon');

  const [emailTargets, setEmailTargets] = useState(null);
  const bulkUpdate = async updates => { try { await api.post('/gst/returns/bulk-update', { ids: [...selected], updates }); toast.success('Updated'); setSelected(new Set()); loadData(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); } };
  const bulkDelete = async () => { try { await api.post('/gst/returns/bulk-delete', { ids: [...selected] }); toast.success('Deleted'); setSelected(new Set()); loadData(); } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); } };
  const bulkEmailTargets = () => { const seen = new Set(); return filtered.filter(r => selected.has(r.id)).filter(r => { if (seen.has(r.client_id)) return false; seen.add(r.client_id); return true; }).map(r => ({ id: r.client_id, client_id: r.client_id, name: r.client_name, email: r.client_email })); };

  const handleBulkWA = () => {
    const rows = filtered.filter(r => selected.has(r.id));
    if (!rows.length) return;
    const msg = `Dear Client,\n\nThis is a reminder that your GST Return is pending filing.\n\nPlease share the required data at the earliest to avoid late fees of ₹50/day.\n\nThank you,\nKrishna Uday & Associates`;
    setWaModal({ targets: rows.map(r => ({ name: r.client_name, whatsapp: r.whatsapp })), defaultMsg: msg });
  };

  const activeFilterCount = Object.values(colFilters).filter(f => f && (Array.isArray(f) ? f.length : (typeof f === 'object' ? f.from || f.to : true))).length;

  const noticeColumns = [
    { key: 'client_name', label: 'Client', render: (v, row) => <div><div className="font-medium text-sm">{v}</div>{row.client_code && <div className="text-xs text-gray-400">{row.client_code}</div>}</div> },
    { key: 'notice_type', label: 'Notice Type' },
    { key: 'date_received', label: 'Received', render: v => fmtDate(v) },
    { key: 'response_due_date', label: 'Reply Due', render: v => {
      const diff = (new Date(v) - new Date()) / 86400000;
      return <span className={diff < 0 ? 'text-red-600 font-semibold' : diff <= 7 ? 'text-orange-600 font-semibold' : ''}>{fmtDate(v)}</span>;
    }},
    { key: 'demand_amount', label: 'Demand', render: v => fmtCurrency(v) },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  const overdue = tab === 'returns' ? filtered.filter(d => d.status === 'Data Pending' || d.status === 'Data Received') : [];

  return (
    <div>
      <PageHeader title="GST" subtitle="Returns & Notices"
        actions={
          <div className="flex gap-2">
            <button onClick={() => downloadExport(tab === 'returns' ? '/export/gst' : '/export/gst-notices', { fy: fyFilter }, `KUA_GST_${tab}_${new Date().toISOString().split('T')[0]}.xlsx`)}
              className="btn-secondary flex items-center gap-2 text-sm"><Download size={15} /> Export</button>
            {canImp && tab === 'returns' && <button onClick={() => setShowImport(true)} className="btn-secondary flex items-center gap-2 text-sm"><Upload size={15} /> Import</button>}
            <button onClick={() => { setEditing(null); setForm({ fy: CURRENT_FY, return_type: 'GSTR-3B', status: 'Data Pending' }); setShowModal(true); }} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Record</button>
          </div>
        } />

      <div className="flex gap-1 mb-4">
        {[['returns', 'GST Returns'], ['notices', 'Notices']].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === t ? 'bg-[#1e3a5f] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>{l}</button>
        ))}
      </div>

      {tab === 'notices' && (
        <div className="card">
          <div className="flex flex-wrap gap-3 mb-4">
            <SearchBar value={search} onChange={setSearch} placeholder="Search client..." />
            <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} className="input-field w-32">
              <option value=''>All Years</option>{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-40">
              <option value="">All Statuses</option>
              {NOTICE_STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <DataTable columns={noticeColumns} data={filteredNotices} />
        </div>
      )}

      {tab === 'returns' && (
        <>
          {overdue.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              ⚠️ <strong>{overdue.length}</strong> GST returns are pending — late fee of ₹50/day will accrue
            </div>
          )}

          <div className="card">
            {/* Toolbar */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
              <SearchBar value={search} onChange={setSearch} placeholder="Search client..." />
              <select value={fyFilter} onChange={e => setFyFilter(e.target.value)} className="input-field w-32">
                <option value=''>All Years</option>{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}
              </select>
              <select value={rtFilter} onChange={e => setRtFilter(e.target.value)} className="input-field w-36">
                <option value="">All Returns</option>
                {GST_RETURN_TYPES.map(r => <option key={r}>{r}</option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-field w-40">
                <option value="">All Statuses</option>
                {GST_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
              {activeFilterCount > 0 && (
                <button onClick={() => setColFilters({})} className="text-xs text-orange-600 border border-orange-200 rounded-lg px-2 py-1.5 hover:bg-orange-50">
                  Clear {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
                </button>
              )}
              <button onClick={() => setShowArchived(a => !a)} className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-300 text-gray-500 hover:border-gray-400'}`}>{showArchived ? '📂 Archived' : '📁 Show Archived'}</button>
              
            </div>

            <BulkActionBar module="gst" count={selected.size} staff={staff} statuses={GST_STATUSES}
              onAssign={id => bulkUpdate({ primary_assignee_id: id })}
              onStatus={s => bulkUpdate({ status: s })}
              onDelete={bulkDelete}
              onWA={handleBulkWA}
              onEmail={() => setEmailTargets(bulkEmailTargets())}
              onClear={() => setSelected(new Set())} />
            <GSTTable
              data={filtered}
              staff={staff}
              selected={selected}
              toggleSelect={toggleSelect}
              toggleAll={toggleAll}
              onEdit={row => { setEditing(row); setForm({ ...row }); setShowModal(true); }}
              onInlineUpdate={handleInlineUpdate}
              onEntryRowSave={loadData}
              onDelete={canDel ? row => setDeleteTarget(row) : null}
              onArchive={async row => { await api.patch(`/gst/returns/${row.id}/${row.is_archived ? 'unarchive' : 'archive'}`); toast.success(row.is_archived ? 'Unarchived' : 'Archived'); loadData(); }}
              showArchived={showArchived} onToggleArchived={() => setShowArchived(a => !a)}
              onSetDueDate={row => setDueDateRecord(row)}
              colFilters={colFilters}
              colUniqueValues={colUniqueValues}
              openColFilter={openColFilter}
              onWA={handleWA}
              onEmail={handleEmail}
              buffers={buffers}
            />
          </div>
        </>
      )}

      {/* Modals */}
      {emailTargets && <BulkEmailModal targets={emailTargets} defaultSubject="Update from Krishna Uday & Associates" onClose={() => setEmailTargets(null)} />}
      {waModal && <WhatsAppModal targets={waModal.targets} defaultMsg={waModal.defaultMsg} onClose={() => setWaModal(null)} />}

      {openFilter && (
        <ColFilterDropdown dateCols={DATE_COLS}
          col={openFilter.col}
          uniqueValues={colUniqueValues[openFilter.col] || []}
          current={colFilters[openFilter.col]}
          onApply={val => applyColFilter(openFilter.col, val)}
          onClose={() => setOpenFilter(null)}
          anchorRef={openFilter.anchorRef}
        />
      )}

      {showModal && (
        <Modal title={tab === 'notices' ? 'Add GST Notice' : (editing ? 'Edit GST Return' : 'Add GST Return')}
          onClose={() => { setShowModal(false); setEditing(null); setForm({}); }}>
          <form onSubmit={e => handleSave(e, false)} className="space-y-4">
            {editing && <p className="text-sm text-gray-600 font-medium">{editing.client_name} — {editing.return_type} {editing.period}</p>}
            {!editing && (
              <FormField label="Client" required>
                <ClientSearchDropdown value={form.client_id || ''} initialLabel="" service="GST"
                  onChange={id => setForm(f => ({ ...f, client_id: id }))} required />
              </FormField>
            )}
            {dupWarning && (
              <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 text-sm text-orange-700">
                <p className="font-semibold mb-2">⚠ Duplicate Warning</p>
                <p>{dupWarning}</p>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => handleSave(null, true)} className="px-4 py-1.5 bg-orange-600 text-white rounded-lg text-xs font-medium">Add Anyway</button>
                  <button type="button" onClick={() => setDupWarning(null)} className="px-4 py-1.5 border border-orange-300 text-orange-700 rounded-lg text-xs font-medium">Cancel</button>
                </div>
              </div>
            )}
            {tab === 'returns' ? (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="FY"><select value={form.fy || CURRENT_FY} onChange={e => setForm(f => ({ ...f, fy: e.target.value }))} className="input-field">{FY_OPTIONS.map(f => <option key={f}>{f}</option>)}</select></FormField>
                <FormField label="Period *"><input required value={form.period || ''} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} className="input-field" placeholder="e.g. Apr 2025" /></FormField>
                <FormField label="Return Type"><select value={form.return_type || 'GSTR-3B'} onChange={e => setForm(f => ({ ...f, return_type: e.target.value }))} className="input-field">{GST_RETURN_TYPES.map(r => <option key={r}>{r}</option>)}</select></FormField>
                <FormField label="Status"><select value={form.status || 'Data Pending'} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="input-field">{GST_STATUSES.map(s => <option key={s}>{s}</option>)}</select></FormField>
                <FormField label="Due Date"><input type="date" value={form.due_date || ''} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Internal Due Date">
                  <div className="flex gap-2">
                    <input type="date" value={form.internal_due_date || ''} onChange={e => setForm(f => ({ ...f, internal_due_date: e.target.value }))} className="input-field flex-1" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, internal_due_date: calcInternalDue(f.due_date, buffers.buffer_gst) }))}
                      className="text-xs border border-gray-200 rounded px-2 py-1.5 text-[#1e3a5f] hover:text-[#f0a500] whitespace-nowrap">Auto</button>
                  </div>
                </FormField>
                <FormField label="Filing Date"><input type="date" value={form.filing_date || ''} onChange={e => setForm(f => ({ ...f, filing_date: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Late Fee"><input type="number" value={form.late_fee || ''} onChange={e => setForm(f => ({ ...f, late_fee: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Primary Assignee">
                  <select value={form.primary_assignee_id || ''} onChange={e => setForm(f => ({ ...f, primary_assignee_id: e.target.value }))} className="input-field">
                    <option value="">Not assigned</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Secondary Assignee">
                  <select value={form.secondary_assignee_id || ''} onChange={e => setForm(f => ({ ...f, secondary_assignee_id: e.target.value }))} className="input-field">
                    <option value="">None</option>
                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </FormField>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Notice Type"><select value={form.notice_type || ''} onChange={e => setForm(f => ({ ...f, notice_type: e.target.value }))} className="input-field"><option value="">Select</option>{NOTICE_TYPES.map(t => <option key={t}>{t}</option>)}</select></FormField>
                <FormField label="Date Received"><input type="date" value={form.date_received || ''} onChange={e => setForm(f => ({ ...f, date_received: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Response Due Date"><input type="date" value={form.response_due_date || ''} onChange={e => setForm(f => ({ ...f, response_due_date: e.target.value }))} className="input-field" /></FormField>
                <FormField label="Demand Amount"><input type="number" value={form.demand_amount || ''} onChange={e => setForm(f => ({ ...f, demand_amount: e.target.value }))} className="input-field" /></FormField>
              </div>
            )}
            <FormField label="Notes"><textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="input-field" rows={2} /></FormField>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary flex-1">Save</button>
              <button type="button" onClick={() => { setShowModal(false); setEditing(null); setForm({}); }} className="btn-secondary flex-1">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {dueDateRecord && (
        <DueDateEditModal record={dueDateRecord} tableName="gst_returns" moduleName="GST"
          onClose={() => setDueDateRecord(null)} onSaved={loadData} />
      )}
      {deleteTarget && (
        <ConfirmModal message="Delete this GST record? This cannot be undone." onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
      {showImport && (
        <ModuleImport module="gst" onComplete={loadData} onClose={() => setShowImport(false)} />
      )}
    </div>
  );
}
